import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentSnapshot,
  type Query,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { db } from "../lib/firebase";
import { fetchFollowingIds } from "../lib/follows";
import { fetchMemberships } from "../lib/memberships";
import { isLegacyPostForPersonalIdentity } from "../lib/identity";
import {
  firebaseRecoveryError,
  isCacheOnlySnapshot,
  isMissingQueryIndexError,
  ListenerGeneration,
  selectRealtimeItems,
} from "../lib/realtimeRecovery";
import type { FirePost } from "../types/models";

const PAGE_SIZE = 12;
const IN_QUERY_LIMIT = 10;

type FeedErrorState = {
  code: string;
  message: string;
  retryable: boolean;
};

type FeedSource = {
  key: string;
  baseQuery: Query;
  liveItems: FirePost[];
  olderItems: FirePost[];
  cursor: DocumentSnapshot | null;
  hasMore: boolean;
  canPaginate: boolean;
  receivedSnapshot: boolean;
  receivedServerSnapshot: boolean;
  itemFilter?: (item: FirePost) => boolean;
  error?: FeedErrorState;
  unsubscribe: () => void;
};

function uniqById(items: FirePost[]) {
  const map = new Map<string, FirePost>();

  for (const item of items) {
    map.set(item.id, item);
  }

  return Array.from(map.values()).sort((a, b) => {
    const aMs =
      typeof a?.createdAt?.toMillis === "function" ? a.createdAt.toMillis() : 0;
    const bMs =
      typeof b?.createdAt?.toMillis === "function" ? b.createdAt.toMillis() : 0;
    return bMs - aMs;
  });
}

function chunk<T>(items: T[], size: number) {
  const out: T[][] = [];

  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }

  return out;
}

function feedErrorState(error: unknown, fallback: string): FeedErrorState {
  const recovery = firebaseRecoveryError(error);
  let message = fallback;

  if (recovery.retryable) {
    message = "Connexion interrompue. Réessaie quand le réseau est disponible.";
  } else if (recovery.code === "failed-precondition") {
    message = "Le fil nécessite une configuration serveur.";
  } else if (recovery.code === "permission-denied") {
    message = "Certains posts ne sont pas accessibles.";
  }

  return { ...recovery, message };
}

export function usePosts(
  user: { uid?: string } | null,
  enabled = true,
  followingOnly = false,
  activeAccountId?: string | null
) {
  const [posts, setPosts] = useState<FirePost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [errorState, setErrorState] = useState<FeedErrorState | null>(null);
  const [offline, setOffline] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const sourcesRef = useRef<FeedSource[]>([]);
  const postsRef = useRef<FirePost[]>([]);
  const loadingMoreRef = useRef(false);
  const queryIdentityRef = useRef("");
  const generationRef = useRef<ListenerGeneration | null>(null);

  if (!generationRef.current) {
    generationRef.current = new ListenerGeneration();
  }

  const userUid = user?.uid ?? "";
  const followIdentityId = activeAccountId || userUid;

  const commitPosts = useCallback((next: FirePost[]) => {
    postsRef.current = next;
    setPosts(next);
  }, []);

  const reload = useCallback(() => {
    setErrorState(null);
    setOffline(false);
    setLoadingPosts(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setReloadKey((prev) => prev + 1);
  }, []);

  const loadMore = useCallback(async () => {
    const generationManager = generationRef.current;
    const generation = generationManager?.current ?? 0;
    const sources = [...sourcesRef.current];
    if (loadingMoreRef.current || !sources.length) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const failures: unknown[] = [];

    try {
      await Promise.all(
        sources.map(async (source) => {
          if (!source.hasMore || !source.cursor) return;

          try {
            const nextSnap = await getDocs(
              query(
                source.baseQuery,
                startAfter(source.cursor),
                limit(PAGE_SIZE)
              )
            );

            if (!nextSnap.docs.length) {
              source.hasMore = false;
              return;
            }

            source.olderItems = [
              ...source.olderItems,
              ...nextSnap.docs
                .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }))
                .filter((item) => !source.itemFilter || source.itemFilter(item)),
            ];
            source.cursor = nextSnap.docs[nextSnap.docs.length - 1] ?? source.cursor;
            source.hasMore = nextSnap.size === PAGE_SIZE;
          } catch (error) {
            failures.push(error);
          }
        })
      );

      if (!generationManager?.isCurrent(generation)) return;

      commitPosts(
        uniqById(
          sourcesRef.current.flatMap((source) => [
            ...source.liveItems,
            ...source.olderItems,
          ])
        )
      );
      setHasMore(sourcesRef.current.some((source) => source.hasMore));
      if (failures.length > 0) {
        setErrorState(
          feedErrorState(failures[0], "Impossible de charger les anciens posts.")
        );
      }
    } finally {
      if (generationManager?.isCurrent(generation)) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [commitPosts]);

  useEffect(() => {
    const generationManager = generationRef.current!;
    const generation = generationManager.restart();
    const queryIdentity = `${userUid}:${followIdentityId}:${followingOnly ? "following" : "all"}`;
    const sameQuery = queryIdentityRef.current === queryIdentity;
    const cachedPosts = sameQuery ? postsRef.current : [];
    queryIdentityRef.current = queryIdentity;

    if (!sameQuery) {
      commitPosts([]);
      setHasMore(false);
    }

    sourcesRef.current = [];
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setErrorState(null);
    setOffline(false);

    if (!enabled) {
      setLoadingPosts(false);
      return () => generationManager.dispose(generation);
    }

    setLoadingPosts(true);
    let bootstrapFinished = false;
    let bootstrapError: FeedErrorState | undefined;
    let receivedServerSnapshot = false;
    let offlineTimer: ReturnType<typeof setTimeout> | undefined;
    const sourceMap = new Map<string, FeedSource>();

    const isCurrent = () => generationManager.isCurrent(generation);

    const clearOfflineTimer = () => {
      if (offlineTimer) clearTimeout(offlineTimer);
      offlineTimer = undefined;
    };

    const updateSnapshotConnectivity = (fromCache: boolean) => {
      if (!isCacheOnlySnapshot(fromCache, receivedServerSnapshot)) {
        receivedServerSnapshot = true;
        clearOfflineTimer();
        setOffline(false);
        return;
      }

      if (offlineTimer) return;
      offlineTimer = setTimeout(() => {
        offlineTimer = undefined;
        if (!isCurrent() || receivedServerSnapshot) return;
        setOffline(true);
        setLoadingPosts(false);
      }, 1_200);
    };

    const syncError = () => {
      if (!isCurrent()) return;
      const sourceErrors = Array.from(sourceMap.values())
        .map((source) => source.error)
        .filter((value): value is FeedErrorState => !!value);
      const nextError =
        sourceErrors.find((value) => !value.retryable) ??
        sourceErrors[0] ??
        bootstrapError ??
        null;
      setErrorState(nextError);
    };

    const pushMerged = () => {
      if (!isCurrent()) return;
      const sources = Array.from(sourceMap.values());
      const fresh = uniqById(
        sources.flatMap((source) => [
          ...source.liveItems,
          ...source.olderItems,
        ])
      );
      const hasSuccessfulSnapshot = sources.some(
        (source) => source.receivedServerSnapshot
      );
      const next = selectRealtimeItems(
        cachedPosts,
        fresh,
        hasSuccessfulSnapshot
      );
      commitPosts(next);
      setHasMore(sources.some((source) => source.hasMore));
      syncError();

      const allSourcesSettled =
        sources.length > 0 &&
        sources.every((source) => source.receivedSnapshot || !!source.error);
      if (
        hasSuccessfulSnapshot ||
        (allSourcesSettled && (next.length > 0 || sources.some((source) => !!source.error))) ||
        (bootstrapFinished && sources.length === 0)
      ) {
        setLoadingPosts(false);
      }
    };

    const subscribeSource = (
      key: string,
      baseQuery: Query,
      errorLabel: string,
      indexFreeFallback?: Query,
      itemFilter?: (item: FirePost) => boolean
    ) => {
      const source: FeedSource = {
        key,
        baseQuery,
        liveItems: [],
        olderItems: [],
        cursor: null,
        hasMore: true,
        canPaginate: true,
        receivedSnapshot: false,
        receivedServerSnapshot: false,
        itemFilter,
        unsubscribe: () => undefined,
      };
      sourceMap.set(key, source);
      sourcesRef.current = Array.from(sourceMap.values());

      const attach = (queryToUse: Query, canUseIndexFallback: boolean) => {
        let active = true;
        let rawUnsubscribe: () => void = () => undefined;
        const cleanup = () => {
          if (!active) return;
          active = false;
          rawUnsubscribe();
        };

        rawUnsubscribe = onSnapshot(
          query(queryToUse, limit(PAGE_SIZE)),
          { includeMetadataChanges: true },
          (snap) => {
            if (!isCurrent()) return;
            source.receivedSnapshot = true;
            source.receivedServerSnapshot = !snap.metadata.fromCache;
            source.error = undefined;
            updateSnapshotConnectivity(snap.metadata.fromCache);
            source.liveItems = snap.docs
              .map((d) => ({ id: d.id, ...(d.data() as any) }))
              .filter(
                (item) =>
                  !item.moderationStatus ||
                  item.moderationStatus === "approved"
              );
            if (itemFilter) source.liveItems = source.liveItems.filter(itemFilter);
            if (!source.olderItems.length) {
              source.cursor = snap.docs[snap.docs.length - 1] ?? null;
            }
            source.hasMore = source.canPaginate
              && (source.olderItems.length
                ? source.hasMore
                : snap.size === PAGE_SIZE);
            pushMerged();
          },
          (error) => {
            if (!isCurrent()) return;
            const nextError = feedErrorState(
              error,
              "Impossible de charger certains posts."
            );
            cleanup();
            if (
              canUseIndexFallback &&
              indexFreeFallback &&
              isMissingQueryIndexError(error)
            ) {
              source.baseQuery = indexFreeFallback;
              source.canPaginate = false;
              source.hasMore = false;
              source.error = undefined;
              attach(indexFreeFallback, false);
              return;
            }
            if (__DEV__) {
              console.warn(errorLabel, {
                code: nextError.code,
                retryable: nextError.retryable,
              });
            }
            source.error = nextError;
            if (nextError.retryable) {
              clearOfflineTimer();
              setOffline(true);
            }
            if (!nextError.retryable) source.hasMore = false;
            pushMerged();
          }
        );

        source.unsubscribe = cleanup;
        generationManager.register(generation, cleanup);
      };

      attach(baseQuery, true);
    };

    async function start() {
      if (!userUid) {
        bootstrapFinished = true;
        pushMerged();
        return;
      }

      try {
        if (followingOnly) {
          const followingIds = await fetchFollowingIds(followIdentityId, 1000);
          if (!isCurrent()) return;

          const followingChunks = chunk(followingIds, IN_QUERY_LIMIT);
          followingChunks.forEach((authorIds, index) => {
            subscribeSource(
              `following-identities-${index}`,
              query(
                collection(db, "posts"),
                where("authorIdentityId", "in", authorIds),
                where("visibility", "==", "global"),
                where("moderationStatus", "==", "approved"),
                orderBy("createdAt", "desc")
              ),
              "identity following posts error",
              query(
                collection(db, "posts"),
                where("authorIdentityId", "in", authorIds),
                where("visibility", "==", "global"),
                where("moderationStatus", "==", "approved")
              )
            );

            subscribeSource(
              `following-legacy-personal-${index}`,
              query(
                collection(db, "posts"),
                where("authorUid", "in", authorIds),
                where("visibility", "==", "global"),
                where("moderationStatus", "==", "approved"),
                orderBy("createdAt", "desc")
              ),
              "legacy personal following posts error",
              query(
                collection(db, "posts"),
                where("authorUid", "in", authorIds),
                where("visibility", "==", "global"),
                where("moderationStatus", "==", "approved")
              ),
              (item) => typeof item.authorUid === "string"
                && authorIds.includes(item.authorUid)
                && isLegacyPostForPersonalIdentity(item, item.authorUid)
            );
          });
          bootstrapFinished = true;
          pushMerged();
          return;
        }

        subscribeSource(
          "global",
          query(
            collection(db, "posts"),
            where("visibility", "==", "global"),
            where("moderationStatus", "==", "approved"),
            orderBy("createdAt", "desc")
          ),
          "global posts error",
          query(
            collection(db, "posts"),
            where("visibility", "==", "global"),
            where("moderationStatus", "==", "approved")
          )
        );

        const [memberships, publicGroupsSnap] = await Promise.all([
          fetchMemberships(userUid, 100),
          getDocs(
            query(
              collection(db, "groups"),
              where("isPublic", "==", true),
              limit(1000)
            )
          ),
        ]);
        if (!isCurrent()) return;

        const publicGroupChunks = chunk(
          publicGroupsSnap.docs.map((groupSnap) => groupSnap.id),
          IN_QUERY_LIMIT
        );

        publicGroupChunks.forEach((groupIds, index) => {
          subscribeSource(
            `public-${index}`,
            query(
              collection(db, "posts"),
              where("visibility", "==", "group"),
              where("groupId", "in", groupIds),
              where("moderationStatus", "==", "approved"),
              orderBy("createdAt", "desc")
            ),
            "public group posts error",
            query(
              collection(db, "posts"),
              where("visibility", "==", "group"),
              where("groupId", "in", groupIds),
              where("moderationStatus", "==", "approved")
            )
          );
        });

        const privateGroupIds = memberships
          .filter((membership) => !membership.isPublic)
          .map((membership) => membership.groupId);

        chunk(privateGroupIds, IN_QUERY_LIMIT).forEach((groupIds, index) => {
          subscribeSource(
            `private-${index}`,
            query(
              collection(db, "posts"),
              where("visibility", "==", "group"),
              where("groupId", "in", groupIds),
              where("moderationStatus", "==", "approved"),
              orderBy("createdAt", "desc")
            ),
            "private group posts error",
            query(
              collection(db, "posts"),
              where("visibility", "==", "group"),
              where("groupId", "in", groupIds),
              where("moderationStatus", "==", "approved")
            )
          );
        });
        bootstrapFinished = true;
        pushMerged();
      } catch (error) {
        if (!isCurrent()) return;
        bootstrapError = feedErrorState(
          error,
          "Impossible de charger certains posts."
        );
        if (__DEV__) {
          console.warn("feed bootstrap error", {
            code: bootstrapError.code,
            retryable: bootstrapError.retryable,
          });
        }
        bootstrapFinished = true;
        pushMerged();
      }
    }

    void start();

    return () => {
      clearOfflineTimer();
      generationManager.dispose(generation);
      if (sourcesRef.current.some((source) => sourceMap.has(source.key))) {
        sourcesRef.current = [];
      }
    };
  }, [commitPosts, enabled, followingOnly, followIdentityId, reloadKey, userUid]);

  return useMemo(
    () => ({
      posts,
      loadingPosts,
      loadingMore,
      hasMore,
      error: errorState?.message ?? null,
      errorCode: errorState?.code ?? null,
      errorRetryable: errorState?.retryable ?? false,
      offline,
      reload,
      loadMore,
    }),
    [
      posts,
      loadingPosts,
      loadingMore,
      hasMore,
      errorState,
      offline,
      reload,
      loadMore,
    ]
  );
}
